<script context="module" lang="ts">
	/** How long an `animated` panel takes to fold. */
	export const PANEL_FOLD_MS = 220;

	/** Nothing slides for somebody who has asked the system for less motion. */
	export function foldDuration(): number {
		if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return PANEL_FOLD_MS;
		return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : PANEL_FOLD_MS;
	}
</script>

<script lang="ts">
	import { slide } from 'svelte/transition';
	/**
	 * Collapsible content section built on the `widget` / `bg-depth` primitives.
	 * Use this instead of Flowbite's `Card` for page sections — it participates
	 * in the depth system, so nesting panels reads correctly in both themes.
	 *
	 * Slots: default (body), `title` (inline, after the caret), `titleRight`
	 * (pushed to the far right of the title bar).
	 */

	/** Heading text. Omit to render an icon-only or slot-only title bar. */
	export let title: string = '';
	/** Font Awesome solid icon name, without the `fa-` prefix. */
	export let faIcon: string = '';
	/** Extra classes for the icon (colour, size). */
	export let faClass: string = '';
	/** Two-way bindable collapse state. */
	export let isHidden: boolean = false;
	/** Tighter padding and a smaller title, for panels inside panels. */
	export let compact: boolean = false;
	/** Drops the widget chrome, leaving only the title bar and body. */
	export let borderless: boolean = false;

	/**
	 * Slides the body open and shut instead of snapping. **Opt-in**, because a transition makes
	 * the body's removal asynchronous, and every panel in the app is written — and tested —
	 * against a body that is gone the moment the panel closes. A caller that moves the page after
	 * folding one waits `PANEL_FOLD_MS` first.
	 */
	export let animated: boolean = false;

	export let titleClass: string = '';
	export let contentClass: string = '';

	let className: string = '';
	export { className as class };

	/**
	 * What may sit in the title bar without collapsing the panel.
	 *
	 * The whole title row is the toggle, so anything interactive placed in the `title` or
	 * `titleRight` slots has to be excluded by hand — and **a tag missing from this list is a
	 * control that works and closes the panel underneath it at the same time**, which reads as the
	 * panel having a mind of its own rather than as a bug in the header. That has already happened
	 * once: `select` was absent, so choosing a dimension from the analytics panels' own dropdowns
	 * collapsed the panel the dimension was for.
	 *
	 * Named form elements plus the ARIA roles a composed control announces itself with, rather than
	 * a bare tag list: `CountrySelect` is an `input` with a `listbox` beside it, and a future
	 * combobox built from `div`s would otherwise be the next thing to trip over this.
	 */
	const INTERACTIVE_IN_TITLE =
		'button, a, input, select, textarea, option, [role="button"], [role="combobox"], [role="listbox"], [role="option"], [role="switch"], [role="menu"], [role="menuitem"], label';

	const fnOnPanelTitleClick = (e: Event) => {
		// Interactive controls placed in the title slots must not toggle the panel.
		if (e.target instanceof HTMLElement) {
			if (e.target.closest(INTERACTIVE_IN_TITLE)) {
				return;
			}
		}

		isHidden = !isHidden;
	};
</script>

<div class="{className} {compact ? 'px-4 py-2' : ''} panelGroup {borderless ? '' : 'widget bg-depth'} group/panelwrapper">
	<!-- svelte-ignore a11y-no-static-element-interactions -->
	<!-- svelte-ignore a11y-click-events-have-key-events -->
	<div
		class="{titleClass} {compact ? 'text-lg' : ''} widget-title group/panel gap-4 flex items-center mb-0 cursor-pointer"
		on:click={fnOnPanelTitleClick}
	>
		{#if faIcon.length > 0}
			<i class="fa-solid fa-{faIcon} {faClass} group-hover/panel:text-primary-500 group-hover/panel:dark:text-primary-500" />
		{/if}
		{#if title.length > 0}
			<span class="shrink-0 group-hover/panel:text-primary-500 group-hover/panel:dark:text-primary-500">{title}</span>
		{/if}
		<i class="fa-solid text-base {isHidden ? 'fa-angle-down' : 'fa-angle-up'} group-hover/panel:text-primary-500 group-hover/panel:dark:text-primary-500" />
		<slot name="title" />
		<span class="grow" />
		<slot name="titleRight" />
	</div>
	<!--
		`animated` outside, `isHidden` inside — the order is the whole of whether it slides. A `|local`
		transition plays only when its **own** enclosing block changes; nested the other way round,
		the element sat inside `{#if animated}`, which never changes, and the panel snapped shut.
	-->
	{#if animated}
		{#if !isHidden}
			<div
				class="{contentClass} panelContent {compact ? 'pb-2 mt-2' : 'mt-5'}"
				transition:slide|local={{ duration: foldDuration() }}
			>
				<slot />
			</div>
		{/if}
	{:else if !isHidden}
		<div class="{contentClass} panelContent {compact ? 'pb-2 mt-2' : 'mt-5'}">
			<slot />
		</div>
	{/if}
</div>
